import type { TaxCalculationInput, TaxCalculationResult, TaxYear } from '../../types/tax';
import { TaxDataManager } from '../taxYear/TaxDataManager';
import { calculateUif } from './calculateUif';
import { calculatePensionContribution } from './calculatePension';
import { calculateTravelAllowanceTaxableIncome } from './calculateTravelAllowance';

export function calculateTax(input: TaxCalculationInput): TaxCalculationResult {
  const { grossIncome, ageCategory, advancedOptions } = input;
  const taxManager = TaxDataManager.getInstance();
  
  // Check if a specific tax year is requested (from TaxCalculationInputWithYear)
  const requestedTaxYear = (input as { taxYear?: TaxYear }).taxYear;
  const originalYear = taxManager.getCurrentTaxYear();
  
  // Temporarily switch tax year if specified
  if (requestedTaxYear && requestedTaxYear !== originalYear) {
    taxManager.setTaxYear(requestedTaxYear);
  }
  
  try {
    // Calculate pension contribution (affects taxable income)
    const pensionResult = advancedOptions?.pension 
      ? calculatePensionContribution(grossIncome, advancedOptions.pension)
      : { taxDeductibleAmount: 0, contributionAmount: 0 };

    // Calculate travel allowance taxable amount (adds to taxable income)
    const travelAllowanceTaxableAmount = advancedOptions?.travelAllowance 
      ? calculateTravelAllowanceTaxableIncome(advancedOptions.travelAllowance)
      : 0;

    // Calculate taxable income after pension deductions and travel allowance additions
    const taxableIncome = grossIncome - pensionResult.taxDeductibleAmount + travelAllowanceTaxableAmount;
    
    // Calculate tax before rebates using current strategy's tax brackets
    let taxBeforeRebates = 0;
    const brackets = taxManager.getTaxBrackets();
    
    for (const bracket of brackets) {
      if (taxableIncome <= bracket.min - 1) {
        break; // Income is below this bracket
      }
      
      // Determine the income portion that falls in this bracket
      const bracketMin = bracket.min;
      const bracketMax = bracket.max || Infinity;
      const incomeInBracket = Math.min(taxableIncome, bracketMax) - Math.max(0, bracketMin - 1);
      
      if (incomeInBracket > 0) {
        // Calculate tax for this bracket
        if (bracket.baseAmount > 0) {
          // Higher brackets with base amount + percentage of excess
          if (taxableIncome > bracketMin - 1) {
            const excessIncome = Math.min(taxableIncome - bracketMin + 1, bracketMax ? bracketMax - bracketMin + 1 : taxableIncome - bracketMin + 1);
            taxBeforeRebates = bracket.baseAmount + (excessIncome * bracket.rate);
          }
        } else {
          // First bracket - just percentage of income
          taxBeforeRebates = Math.min(taxableIncome, bracketMax) * bracket.rate;
        }
      }
    }
    
    // Calculate rebates based on age category using current strategy
    const taxRebates = taxManager.getTotalRebate(ageCategory);
    
    // Calculate medical aid tax credits using current strategy
    const medicalAidCredit = advancedOptions?.medicalAid 
      ? taxManager.getMedicalAidCredit(
          advancedOptions.medicalAid.mainMember,
          advancedOptions.medicalAid.dependents
        )
      : 0;
    
    // Calculate final tax after rebates and credits
    const taxAfterRebatesAndCredits = Math.max(0, taxBeforeRebates - taxRebates - medicalAidCredit);
    
    // Calculate UIF contribution
    const uifContribution = calculateUif(grossIncome);
    
    // Calculate total deductions
    const totalDeductions = taxAfterRebatesAndCredits + uifContribution + pensionResult.contributionAmount;
    
    // Calculate net income
    const netIncome = grossIncome - totalDeductions;
    
    return {
      grossIncome,
      taxableIncome,
      taxBeforeRebates,
      taxRebates,
      medicalAidCredit,
      taxAfterRebatesAndCredits,
      pensionContribution: pensionResult.contributionAmount,
      travelAllowanceTaxableAmount,
      uifContribution,
      totalDeductions,
      netIncome,
    };
  } finally {
    // Restore original tax year if we switched
    if (requestedTaxYear && requestedTaxYear !== originalYear) {
      taxManager.setTaxYear(originalYear);
    }
  }
}

// Convenience function that temporarily switches tax year for calculation
export function calculateTaxForYear(input: TaxCalculationInput, taxYear: string): TaxCalculationResult {
  const taxManager = TaxDataManager.getInstance();
  const originalYear = taxManager.getCurrentTaxYear();
  
  try {
    taxManager.setTaxYear(taxYear as TaxYear);
    return calculateTax(input);
  } finally {
    // Always restore original tax year
    taxManager.setTaxYear(originalYear);
  }
}
